'use client';

import { useEffect, useRef, forwardRef, useCallback } from 'react';
import intlTelInput, { Iti } from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';
import styles from '@/components/HeroBlockBlog/components/PhoneInput/phone-input.module.scss';

interface ContactPhoneInputProps {
  label?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  error?: string;
}

const ContactPhoneInput = forwardRef<HTMLInputElement, ContactPhoneInputProps>(
  (
    {
      label,
      placeholder = 'Enter your phone number',
      value = '',
      onChange,
      className = '',
      error,
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const itiRef = useRef<Iti | null>(null);
    const onChangeRef = useRef(onChange);

    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
      if (!inputRef.current || itiRef.current) return;

      itiRef.current = intlTelInput(inputRef.current, {
        initialCountry: 'us',
        preferredCountries: ['us', 'gb', 'ca', 'au'],
        separateDialCode: true,
        formatOnDisplay: true,
        autoPlaceholder: 'aggressive',
        customPlaceholder: (selectedCountryPlaceholder: string) =>
          placeholder || selectedCountryPlaceholder,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);

      const pushToForm = () => {
        if (!onChangeRef.current) return;
        const raw = inputRef.current?.value || '';
        let phone = (itiRef.current?.getNumber() as string) || '';
        if (!phone && raw) {
          const dialCode =
            itiRef.current?.getSelectedCountryData()?.dialCode || '';
          phone = raw.startsWith('+')
            ? raw
            : dialCode
              ? `+${dialCode}${raw}`
              : raw;
        }
        onChangeRef.current(phone);
      };

      const handleInput = () => pushToForm();
      const handleCountryChange = () => pushToForm();

      const handleKeyDown = (e: KeyboardEvent) => {
        const allowedKeys = [
          'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
          'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab', 'Enter',
        ];
        if (allowedKeys.includes(e.key)) return;
        if (e.ctrlKey || e.metaKey) return;
        if (/^\d$/.test(e.key)) return;
        if (e.key === '+' && (inputRef.current?.selectionStart ?? 0) === 0) return;
        e.preventDefault();
      };

      const el = inputRef.current;
      el.addEventListener('keydown', handleKeyDown);
      el.addEventListener('input', handleInput);
      el.addEventListener('blur', handleInput);
      el.addEventListener('keyup', handleInput);
      el.addEventListener('countrychange', handleCountryChange);

      return () => {
        el.removeEventListener('keydown', handleKeyDown);
        el.removeEventListener('input', handleInput);
        el.removeEventListener('blur', handleInput);
        el.removeEventListener('keyup', handleInput);
        el.removeEventListener('countrychange', handleCountryChange);
        try {
          itiRef.current?.destroy();
        } catch {}
        itiRef.current = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (!itiRef.current || !inputRef.current) return;

      const currentNumber = itiRef.current.getNumber();

      if (!value || value === '+') {
        if (inputRef.current.value !== '') {
          inputRef.current.value = '';
          itiRef.current.setNumber('');
        }
        return;
      }

      if (typeof value === 'string' && value !== currentNumber) {
        try {
          itiRef.current.setNumber(value);
        } catch {}
      }
    }, [value]);

    const handleRef = useCallback(
      (node: HTMLInputElement | null) => {
        if (typeof ref === 'function') ref(node as HTMLInputElement);
        else if (ref)
          (ref as React.MutableRefObject<HTMLInputElement | null>).current =
            node;
        inputRef.current = node;
      },
      [ref]
    );

    return (
      <div className={[styles.root, className].join(' ')}>
        {label && <label className={styles.label}>{label}</label>}
        <div className={styles['input-container']}>
          <input
            ref={handleRef}
            type="tel"
            className={`${styles.input} ${error ? styles.error : ''}`}
            placeholder={placeholder}
          />
        </div>
        {error && <div className={styles.errorMessage}>{error}</div>}
      </div>
    );
  }
);

ContactPhoneInput.displayName = 'ContactPhoneInput';

export default ContactPhoneInput;
